<script lang="ts">
  import type { Distributor } from "@shared/types/Distributor";
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";
  import Flash from "@shared/services/flashv2.js";
  import AddProductModal from "./AddProductModal.svelte";
  import Authorization from "@shared/svelte/Components/Authorization.svelte";
  import PageWrapper from "@retailer/svelte/Shared/PageWrapper.svelte";
  import UIRouterLink from "@shared/svelte/Components/UIRouterLink.svelte";
  import Arrow from "@shared/svelte/Components/Arrow.svelte";
  import SettingsPageWrapper from "@modern/components/retailer/settings/PageWrapper.svelte";

  interface CustomPriceInterface {
    case_price: number;
    created_at: string;
    distributor_id: number;
    fintech: boolean;
    id: number;
    name: string;
    product_id: number;
    retailer_id: number;
    unit_price: number;
  }
  export let distributors: Distributor[];
  export let customPrices: CustomPriceInterface[];
  export let distributorId: number;
  export let productSearch: string;

  let showAddProductModal = false;

  let distributor: Distributor = distributors.find(d => d.id == distributorId);
  const distributorName = distributor?.name || "";

  //
  // === Custom Price Editing ===
  //
  let priceEditing = {};
  let priceEditUnit = {};
  let priceEditCase = {};
  let priceEditSubmitPending = {};

  const startEditingPrice = function (product) {
    priceEditing[product.product_id] = true;
    priceEditUnit[product.product_id] = product.unit_price;
    priceEditCase[product.product_id] = product.case_price;
    priceEditSubmitPending[product.product_id] = false;
  };

  const cancelEditingPrice = function (id) {
    delete priceEditing[id];
    delete priceEditUnit[id];
    delete priceEditCase[id];
    delete priceEditSubmitPending[id];
    priceEditing = priceEditing;
    priceEditUnit = priceEditUnit;
    priceEditCase = priceEditCase;
    priceEditSubmitPending = priceEditSubmitPending;
  };

  const submitPriceEdits = async function (product) {
    priceEditSubmitPending[product.product_id] = true;

    let data: { custom_price: any, distributor_id: number } = {
      custom_price: {},
      distributor_id: distributorId
    };

    if (priceEditUnit[product.product_id] != null && !isNaN(priceEditUnit[product.product_id])) {
      data.custom_price.unit_price = priceEditUnit[product.product_id];
    }

    if (priceEditCase[product.product_id] != null && !isNaN(priceEditCase[product.product_id])) {
      data.custom_price.case_price = priceEditCase[product.product_id];
    }

    try {
      const res = await HttpPromise({
        method: "PUT",
        url: "/api/retailer/products/" + product.product_id,
        data: data,
      });
      cancelEditingPrice(product.product_id);
      for (var i = 0; i < customPrices.length; i++) {
        if (customPrices[i].product_id == res.id) {
          customPrices[i].unit_price = res.unit_price;
          customPrices[i].case_price = res.case_price;
          break;
        }
      }
    } catch (e) {
      delete priceEditSubmitPending[product.product_id];
      Flash.now({
        type: "error",
        header: "Error",
        message: "There was an error adding custom pricing for this product.",
      });
    }
  };

  const openAddProductModal = function () {
    showAddProductModal = true;
  };

  const deleteCustomPricing = async function (customPriceId) {
    if (!confirm("Are you sure you want to delete this custom pricing information?")) return;

    try {
      const res = await HttpPromise({
        method: "DELETE",
        url: "/api/retailer/custom_prices/" + customPriceId,
      });
      for (var i = 0; i < customPrices.length; i++) {
        if (customPrices[i].id == customPriceId) {
          customPrices.splice(i, 1);
          customPrices = customPrices;
          break;
        }
      }
    } catch (e) {
      Flash.now({
        type: "error",
        header: "Error",
        message: "There was an error deleting this custom pricing information.",
      });
    }
  };

  const addProductSuccessCb = function (res, product) {
    customPrices.push(res);
    let item = customPrices[customPrices.length - 1];
    item.name = product.name;
    showAddProductModal = false;
    startEditingPrice(item);
  };

  let addProduct: {
    filter: string | null;
  } = {
    filter: null,
  };
  let keepPrefilled = false;

  if (productSearch) {
    addProduct.filter = productSearch;
    keepPrefilled = true;
    openAddProductModal();
  } else {
    showAddProductModal = false;
    keepPrefilled = false;
  }
</script>

<SettingsPageWrapper>
  <PageWrapper let:mobileMode>
    <!-- SETTINGS CONTAINER -->
    <div class="padding--small">
      <!-- SETTINGS HEADER -->
      <div class="grid-row padding-bottom--smaller padding-bottom--small--md">
        <div class="grid-col c12-12 c6-12--md vertical-align-middle">
          <UIRouterLink stateName={"layout.pricing"} params={{}} classes={""}>
            <span class="color-brandPrimary cursor-pointer margin-right--smaller">
              <Arrow direction={"left"} />
            </span>
            <span class="s4--md notranslate">{distributorName}</span>
          </UIRouterLink>
        </div>
      </div>
      <!-- // SETTINGS HEADER -->

      {#if distributor && !distributor.integrated && (!distributor.seven_fifty_integration || !distributor.uses_price_zones)}
        <div class="padding-top--small--md">
          {#if !mobileMode}
            <div class="grid-row">
              <div class="grid-col c6-12">
                <div class="w600 margin-bottom--small">Account Pricing</div>
              </div>

              <div class="grid-col c6-12 align-right">
                <Authorization authName={"custom_prices.create"}>
                  <button
                    class="button button--small button--primary button--label"
                    on:click={openAddProductModal}
                  >
                    Add Product
                  </button>
                </Authorization>
              </div>
            </div>
          {/if}

          {#if !mobileMode}
            <div class="padding-left--default padding-right--default padding-bottom--smaller">
              <div class="grid-row opacity-60">
                <div class="grid-col c2-12">
                  <div class="s6">Product</div>
                </div>
                <div class="grid-col c3-12">
                  <div class="s6 align-center">Uploaded</div>
                </div>
                <div class="grid-col c2-12 align-center">
                  <div class="s6">Unit Price</div>
                </div>
                <div class="grid-col c2-12 align-center">
                  <div class="s6">Case Price</div>
                </div>
              </div>
            </div>
          {/if}

          <div class="tile pricing-details-tile">
            {#if mobileMode}
              <div class="tile__row hide--md padding--smaller w600">
                <div class="media">
                  <div class="media-fluid">Account Pricing</div>
                  <div>
                    <Authorization authName={"custom_prices.create"}>
                      <button
                        class="button button--small button--primary button--label"
                        on:click={openAddProductModal}
                      >
                        Add Price
                      </button>
                    </Authorization>
                  </div>
                </div>
              </div>
            {/if}

            <!-- PRODUCT SPECIFIC PRICE -->
            {#each customPrices as product}
              <div
                class="padding--smaller padding--1--md tile__row settings__editable-row"
                class:settings__editable-row--editing={priceEditing[product.product_id]}
              >
                <div class="grid-row align--middle">
                  <div class="grid-col c12-12 c2-12--md">
                    <div class="s6 ellipsis notranslate">
                      {product.name}
                    </div>
                  </div>
                  <div class="grid-col c12-12 c3-12--md align-center-md">
                    <div class="s6 ellipsis">
                      <span class="w600 hide--md">Uploaded:</span>
                      {#if product.fintech}
                        <span>Fintech</span>
                      {:else}
                        <span>Custom</span>
                      {/if}
                    </div>
                  </div>
                  <div class="grid-col c12-12 c2-12--md align-center-md">
                    {#if !priceEditing[product.product_id]}
                      <div class="s6 ellipsis">
                        <span class="w600 hide--md">Unit Price: </span>
                        {#if product.unit_price}
                          <span>${product.unit_price.toFixed(2)}</span>
                        {:else}
                          <span class="opacity-50">None Set</span>
                        {/if}
                      </div>
                    {/if}
                    {#if priceEditing[product.product_id]}
                      <div>
                        <div class="s6 strong margin-top--smaller hide--md">Unit Price</div>
                        <div class="input-wrapper input-wrapper--small">
                          <input
                            class="input input--small"
                            type="number"
                            id="priceEditUnit-{product.product_id}"
                            bind:value={priceEditUnit[product.product_id]}
                            placeholder="Unit"
                          />
                        </div>
                      </div>
                    {/if}
                  </div>
                  <div
                    class="grid-col c12-12 c2-12--md align-center-md margin-bottom--small margin-bottom--none--md"
                  >
                    {#if !priceEditing[product.product_id]}
                      <div class="s6 ellipsis">
                        <span class="w600 hide--md">Case Price: </span>
                        {#if product.case_price}
                          <span>${product.case_price.toFixed(2)}</span>
                        {:else}
                          <span class="opacity-50">None Set</span>
                        {/if}
                      </div>
                    {/if}
                    {#if priceEditing[product.product_id]}
                      <div>
                        <div class="s6 strong margin-top--smaller hide--md">Case Price</div>
                        <div class="input-wrapper input-wrapper--small">
                          <input
                            class="input input--small"
                            type="number"
                            id="priceEditCase-{product.product_id}"
                            bind:value={priceEditCase[product.product_id]}
                            placeholder="Case"
                          />
                        </div>
                      </div>
                    {/if}
                  </div>
                  <Authorization authName={"custom_prices.create"}>
                    <div class="grid-col c12-12 c3-12--md align-right-md">
                      {#if !priceEditing[product.product_id]}
                        <button
                          class="button button--label button--secondary"
                          on:click={() => startEditingPrice(product)}>Edit</button
                        >
                        <button
                          class="button button--label button--alert"
                          on:click={() => deleteCustomPricing(product.id)}>Delete</button
                        >
                      {:else}
                        <button
                          class="button button--label button--primary"
                          disabled={priceEditSubmitPending[product.product_id]}
                          on:click={() => submitPriceEdits(product)}>Save</button
                        >
                        <button
                          class="button button--label button--secondary"
                          disabled={priceEditSubmitPending[product.product_id]}
                          on:click={() => cancelEditingPrice(product.product_id)}>Cancel</button
                        >
                      {/if}
                    </div>
                  </Authorization>
                </div>
              </div>
            {/each}
            <!-- PRODUCT SPECIFIC PRICE -->
            {#if customPrices.length === 0}
              <div class="align-center padding--default">
                <div class="s3 opacity-50">No custom pricing data added for this distributor.</div>
              </div>
            {/if}
            <!-- PRODUCT SPECIFIC PRICE -->
          </div>
        </div>
      {:else}
        <div class="padding-top--small--md">
          <div class="align-center padding--default">
            <div class="s3 opacity-50">We do not support custom pricing for this distributor.</div>
          </div>
        </div>
      {/if}
    </div>

    <AddProductModal
      bind:keepPrefilled
      {distributor}
      {addProduct}
      bind:showAddProductModal
      {addProductSuccessCb}
      {mobileMode}
    />
  </PageWrapper>
  <!-- SETTINGS CONTAINER -->
</SettingsPageWrapper>
